import React, { type ReactElement } from "react";
import { useAppSelector, useAppDispatch } from "../../../../../../../../util/store-types";
import ProductImage from "components/source/shared/product-image";
import ProductImagePath from "helpers/product-image-path";
import {
  getProductImageAltText,
  getProductImageUrlsByOrientation,
  ProductImageOrientation,
  ProductImageSize,
} from "helpers/product-image-helpers";
import SectionHeader from "../components/sm-bag-section-header";
import SimplifyMembershipBagActions from "actions/sm-bag-actions";
import { formatDateMonthDayCondensed } from "helpers/date-helpers";
import { HELP } from "routes";

import type { BagItem } from "@rtr/godmother";
import type ProductImageUrlSet from "@rtr/godmother/dist/types/ProductImageUrlSet";

interface SMBagReturningItemsProps {
  returningItemsRef?: React.RefObject<HTMLDivElement>;
  scrollToKeeping?: () => void;
}

function SMBagReturningItems({ scrollToKeeping = () => {}, returningItemsRef }: SMBagReturningItemsProps) {
  const dispatch = useAppDispatch();
  const returnByDate = useAppSelector(state => state.membershipBag?.crossShipReturnByDateForToday) || "";
  const itemsToReturnRedux = useAppSelector(state => state.smBag?.items?.returning) || [];

  const [itemsToReturnLocal, setItemsToReturnLocal] = React.useState<BagItem[]>(itemsToReturnRedux);

  const isReturningItems = itemsToReturnLocal?.length > 0;

  React.useEffect(() => {
    setItemsToReturnLocal(oldItems => {
      if (itemsToReturnRedux?.length < oldItems?.length) {
        scrollToKeeping();
      }

      return itemsToReturnRedux;
    });
  }, [itemsToReturnRedux, scrollToKeeping]);

  const fallBackThumbnailImgSrc = (gridItem: BagItem): string => {
    return gridItem?.images?.[ProductImageOrientation.NO_MODEL.path as keyof ProductImageUrlSet]
      ? ProductImagePath +
          gridItem.images?.[ProductImageOrientation.NO_MODEL.path as keyof ProductImageUrlSet]?.[
            ProductImageSize.x270.path
          ]
      : ProductImagePath +
          gridItem?.images?.[ProductImageOrientation.FRONT.path as keyof ProductImageUrlSet]?.[
            ProductImageSize.x270.path
          ];
  };

  const handleKeep = (item: BagItem): void => {
    dispatch(SimplifyMembershipBagActions.setKeepPromise(item.bookingId));
  };

  const handleHeader = (): ReactElement => {
    return isReturningItems ? (
      <SectionHeader
        headerText={`Return by ${formatDateMonthDayCondensed(returnByDate)}`}
        subHeaderText="Want to hold onto an item? Tap to keep it longer"
        subheaderLink={HELP.MEMBERSHIP_LATE_FEES}
        subheaderLinkText="Learn More"
      />
    ) : (
      <SectionHeader headerText="Returning" />
    );
  };

  return (
    <div className={`section-container returning-items remove-border`} ref={returningItemsRef}>
      {handleHeader()}

      <div className="returning-items-grid">
        {isReturningItems ? (
          itemsToReturnLocal.map(item => (
            <div key={item.bookingId} className="returning-items-grid-item-container button-separate">
              <ProductImage
                className={"thumbnail  button-separate"}
                altText={getProductImageAltText(item.displayName, item.designerName)}
                imageGroup={getProductImageUrlsByOrientation(item.images, [ProductImageOrientation.NO_MODEL.path])}
                imageSize={ProductImageSize.x270}
                fallbackSrc={fallBackThumbnailImgSrc(item)}
              />
              <button className="keep-item  button-separate" onClick={() => handleKeep(item)}>
                <span className="universal-small--semibold">Keep</span>
              </button>
            </div>
          ))
        ) : (
          <span className="universal-medium empty-text">Items you swap will appear here</span>
        )}
      </div>
    </div>
  );
}

export default SMBagReturningItems;
